import { useMemo } from "react";
import { isMock, type RuntimeConfig } from "../config";
import { MOCK_PASSKEYS } from "../mocks/fixtures";
import { cognitoApi, type CognitoApi, type PasskeyRecord } from "./cognito-api";

/** The Cognito browser client, or in mock builds one that answers from fixtures so the Security page can be walked. */
export function useCognitoApi(config: RuntimeConfig): CognitoApi {
  return useMemo(() => (isMock ? mockCognitoApi() : cognitoApi(config)), [config]);
}

/**
 * The mock's passkeys persist in localStorage (nudge template pitfall 2): the Security page and the
 * passkey nudge each hold a client, and a mock sign-in is a full navigation — module state would
 * bring a removed passkey back and the nudge would never show.
 */
const PASSKEYS_KEY = "lms.mock.passkeys";
function mockPasskeys(): PasskeyRecord[] {
  try {
    const raw = localStorage.getItem(PASSKEYS_KEY);
    return raw ? (JSON.parse(raw) as PasskeyRecord[]) : [...MOCK_PASSKEYS];
  } catch {
    return [...MOCK_PASSKEYS];
  }
}
function saveMockPasskeys(passkeys: PasskeyRecord[]): void {
  try {
    localStorage.setItem(PASSKEYS_KEY, JSON.stringify(passkeys));
  } catch {
    // private mode: the change lives for this page only
  }
}

function mockCognitoApi(): CognitoApi {
  const fresh = () => ({ AuthenticationResult: { IdToken: "mock.e30=", AccessToken: "mock", ExpiresIn: 3600 } });
  const delay = <T,>(v: T) => new Promise<T>((r) => setTimeout(() => r(v), 30));
  return {
    listPasskeys: () => delay({ Credentials: mockPasskeys() }),
    startPasskey: () => delay({ CredentialCreationOptions: {} }),
    completePasskey: () => {
      saveMockPasskeys([...mockPasskeys(), { CredentialId: `pk-${Date.now()}`, FriendlyCredentialName: "This device", AuthenticatorAttachment: "platform", CreatedAt: Math.floor(Date.now() / 1000), RelyingPartyId: window.location.hostname }]);
      return delay({});
    },
    deletePasskey: (_t, id) => {
      saveMockPasskeys(mockPasskeys().filter((p) => p.CredentialId !== id));
      return delay({});
    },
    changePassword: () => delay({}),
    associateSoftwareToken: () => delay({ SecretCode: "JBSWY3DPEHPK3PXP" }),
    verifySoftwareToken: (_t, code) => delay({ Status: code === "123456" ? "SUCCESS" : "ERROR" }),
    setTotpPreference: () => delay({}),
    globalSignOut: () => delay({}),
    initiateAuth: (_u, preferred, password) => delay(preferred === "PASSWORD" && password ? { ChallengeName: "SOFTWARE_TOKEN_MFA", Session: "s" } : { ChallengeName: "WEB_AUTHN", Session: "s", ChallengeParameters: { CREDENTIAL_REQUEST_OPTIONS: "{}" } }),
    respondToChallenge: () => delay(fresh()),
  };
}
